import React, { FC, useEffect, useState } from 'react';
import { useSnackbar } from '@Providers/useSnackbar';
import { useAppThunkDispatch, useTypedSelector } from '@Features/store';
import {
	videoProviderErrorMessage,
	videoProvidersUpdate,
} from '../Slices/VideoProviderSettings.slice';
import { VideoProviderSetting } from '../Types/VideoProviders.types';
import {
	buildCredentialsPatch,
	CardNote,
	emptySecretDraft,
	ProviderCard,
	SecretDraft,
	SecretField,
	storedSecret,
} from './ProviderCardShared';

type Props = {
	setting: VideoProviderSetting;
};

const GoogleDriveCard: FC<Props> = ({ setting }) => {
	const dispatch = useAppThunkDispatch();
	const { displaySnackbar } = useSnackbar();
	const { loaders } = useTypedSelector((state) => state.VideoProviderSettings);

	const [enabled, setEnabled] = useState(setting.enabled);
	const [keyDraft, setKeyDraft] = useState<SecretDraft>(emptySecretDraft());

	useEffect(() => {
		setEnabled(setting.enabled);
		setKeyDraft(emptySecretDraft());
	}, [setting]);

	const save = async () => {
		try {
			await dispatch(
				videoProvidersUpdate({
					provider: 'google_drive',
					enabled,
					credentials: buildCredentialsPatch({ api_key: keyDraft }),
				})
			).unwrap();
			displaySnackbar('success', 'Google Drive settings saved');
		} catch (error: any) {
			displaySnackbar(
				'error',
				videoProviderErrorMessage(error, 'Unable to save Google Drive settings')
			);
		}
	};

	return (
		<ProviderCard
			title="Google Drive"
			subtitle="Source for imports — files shared as 'anyone with the link'"
			enabled={enabled}
			onEnabledChange={setEnabled}
			onSave={save}
			saving={loaders.update === 'pending'}
		>
			<SecretField
				label="Google API key"
				stored={storedSecret(setting.credentials, 'api_key')}
				draft={keyDraft}
				onChange={setKeyDraft}
			/>
			<CardNote>
				Used for the metadata preflight and the direct-download URL both
				providers fetch from.
			</CardNote>
		</ProviderCard>
	);
};

export default GoogleDriveCard;
